import React, { useState } from 'react';
import {
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Box,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { useOnboardingStore } from '../../../stores/onboardingStore';

const CoursesStep: React.FC = () => {
  const { userData, updateUserData } = useOnboardingStore();
  
  const [favoriteInput, setFavoriteInput] = useState('');
  const [challengingInput, setChallengingInput] = useState('');
  
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const favoriteColor = useColorModeValue('green.500', 'green.400');
  const challengingColor = useColorModeValue('red.500', 'red.400');

  const addFavoriteCourse = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && favoriteInput.trim()) {
      e.preventDefault();
      const currentCourses = userData.favoriteCourses;
      if (!currentCourses.includes(favoriteInput.trim()) && currentCourses.length < 5) {
        updateUserData('favoriteCourses', [...currentCourses, favoriteInput.trim()]);
        setFavoriteInput('');
      }
    }
  };

  const addChallengingCourse = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && challengingInput.trim()) {
      e.preventDefault();
      const currentCourses = userData.challengingCourses;
      if (!currentCourses.includes(challengingInput.trim()) && currentCourses.length < 5) {
        updateUserData('challengingCourses', [...currentCourses, challengingInput.trim()]);
        setChallengingInput('');
      }
    }
  };

  const removeFavoriteCourse = (course: string) => {
    updateUserData('favoriteCourses', userData.favoriteCourses.filter(c => c !== course));
  };

  const removeChallengingCourse = (course: string) => {
    updateUserData('challengingCourses', userData.challengingCourses.filter(c => c !== course));
  };

  return (
    <VStack spacing={8} align="stretch" justify="center" maxW="800px" mx="auto" w="full">
        {/* Header */}
        <VStack spacing={4} textAlign="center">
          <Heading size="2xl">Your Courses</Heading>
          <Text color={textColor} fontSize="lg">
            Tell us about your favorite subjects and areas where you'd like extra help.
          </Text>
        </VStack>

      {/* Favorite Courses */}
      <FormControl>
        <FormLabel color={favoriteColor}>
          Favorite Courses (Max 5)
        </FormLabel>
        <Input
          placeholder="Type a course name and press Enter"
          value={favoriteInput}
          onChange={(e) => setFavoriteInput(e.target.value)}
          onKeyDown={addFavoriteCourse}
          bg={inputBg}
          isDisabled={userData.favoriteCourses.length >= 5}
        />
        <Wrap mt={3}>
          {userData.favoriteCourses.map((course) => (
            <WrapItem key={course}>
              <Tag size="lg" colorScheme="green" borderRadius="full">
                <TagLabel>{course}</TagLabel>
                <TagCloseButton onClick={() => removeFavoriteCourse(course)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
        <Text fontSize="sm" color={textColor} mt={2}>
          {userData.favoriteCourses.length}/5 courses selected
        </Text>
      </FormControl>

      <Divider />

      {/* Challenging Courses */}
      <FormControl>
        <FormLabel color={challengingColor}>
          Challenging Courses (Max 5)
        </FormLabel>
        <Input
          placeholder="Type a course name and press Enter"
          value={challengingInput}
          onChange={(e) => setChallengingInput(e.target.value)}
          onKeyDown={addChallengingCourse}
          bg={inputBg}
          isDisabled={userData.challengingCourses.length >= 5}
        />
        <Wrap mt={3}>
          {userData.challengingCourses.map((course) => (
            <WrapItem key={course}>
              <Tag size="lg" colorScheme="red" borderRadius="full">
                <TagLabel>{course}</TagLabel>
                <TagCloseButton onClick={() => removeChallengingCourse(course)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
        <Text fontSize="sm" color={textColor} mt={2}>
          {userData.challengingCourses.length}/5 courses selected
        </Text>
      </FormControl>

        <Box>
          <Text fontSize="sm" color={textColor}>
            Tip: Add courses like "Calculus", "Physics", "Computer Science", "English Literature", "History", "Psychology", "Art", etc.
          </Text>
        </Box>
      </VStack>
  );
};

export default CoursesStep;